import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useEditableMode } from '@cad-arc';
import { autorun, reaction, runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { v4 } from 'uuid';
import { useIntegratedActionBroadcaster } from '../../action-broadcaster/use-integrated-action-broadcaster';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useRemoveEventNotifications } from '../../event-notifications/use-event-notifications';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { useWorkspacePanel } from '../../panels/hooks/use-workspace-panel';
import { usePhoneCallsStore } from '../../phone-calls/use-phone-calls';
import { useSetEventPanelModal } from '../event-panel-modals';
import { useFormHasHadDataSetAtLeastOnceContext } from '../event-state/form-has-had-data-set-at-least-once';
import { RefreshEventPanelProvider } from '../refresh-event-panel';
import { EventPanelUnitsProvider } from '../units/event-panel-units-provider';
import { useOpenEventPanel } from '../use-open-event-panel';
import { EventManagerRegister } from './event-manager-register';
import { EventMetadataProvider } from './event-metadata';
import { EventPanelActionHandler } from './event-panel-action-handler';
import { eventPanelNotificationGroupToNotifications } from '../../event-notifications/event-panel-event-notification-groups';
import { useIntegratedEventState } from '../event-state/use-integrated-event-state';
import { useEventApi } from '../event-api/use-event-api';
import { useEntityPanelManager } from '../../panels/entity-panels/monitored-entities/state/entity-panel-manager';
import { CadNextNotificationTypeEnumType } from '@mark43/cad-api';
import { eventPanelTabConfigs } from '../tabs/components/event-panel-tabs-config';

function notificationBelongsToTab(notification: CadNextNotificationTypeEnumType) {
    const notificationTypes = Object.values(eventPanelTabConfigs).flatMap((config) =>
        'notificationTypes' in config ? config.notificationTypes : []
    );
    return notificationTypes.includes(notification);
}

export const IntegratedEventPanelForm = observer(function IntegratedEventPanelForm(props: {
    eventId?: number;
    children: React.ReactNode;
}): React.ReactElement {
    const workspacePanel = useWorkspacePanel<'EVENT'>();
    const phoneCallsStore = usePhoneCallsStore();
    const { editUnassignedEvents, editAssignedEvents, proQaEnhancementsEnabled, editClearedEvents } =
        useEnabledFeatures();
    const { eventPanelOptions } = useBootstrapState();
    const [editableMode, setEditableMode] = useEditableMode('edit');
    const openEventPanel = useOpenEventPanel();
    const formHasHadDataSetAtLeastOnce = useFormHasHadDataSetAtLeastOnceContext();
    const setEventPanelModal = useSetEventPanelModal();
    const { trigger } = useIntegratedActionBroadcaster();
    const eventPanelManager = useEntityPanelManager();

    useEffect(() => {
        return reaction(
            () => workspacePanel.isFocused,
            (val) => {
                if (
                    val &&
                    workspacePanel.linkedEntityId &&
                    eventPanelManager.historicalEventPanels[workspacePanel.linkedEntityId]
                ) {
                    eventPanelManager.historicalEventPanels[
                        workspacePanel.linkedEntityId
                    ].lastFocused = Date.now();
                }
            }
        );
    }, [workspacePanel, eventPanelManager]);

    const {
        eventForm,
        eventQuery: newOrExistingEventQuery,
        refreshEventData: refreshEventPanel,
        eventMetadataStore,
    } = useIntegratedEventState({
        eventId: workspacePanel.linkedEntityId,
        queryKey: workspacePanel.uuid,
        initialPhoneCall: workspacePanel.getPanelState('phoneCallDto'),
        initialLocation: workspacePanel.getPanelState('location'),
        selfInitiate: workspacePanel.getPanelState('selfInitiate'),
        onRefresh({ eventPanelView, location }) {
            runInAction(() => {
                if (eventPanelView.form.eventId && eventPanelView.metadata) {
                    workspacePanel.setLinkedEntityId(eventPanelView.form.eventId);
                    workspacePanel.setLinkedEntityType(
                        eventPanelView.metadata?.isScheduledEvent
                            ? 'SCHEDULED_AGENCY_EVENT'
                            : 'CAD_AGENCY_EVENT'
                    );
                    workspacePanel.setWorkspacePanelType(
                        eventPanelView.metadata?.isScheduledEvent ? 'SCHEDULED_EVENT' : 'EVENT'
                    );
                }
            });
            if (location && !eventPanelView.form.location) {
                trigger([
                    {
                        id: v4(),
                        targetWindowType: 'EVENT_V2',
                        type: 'SUBMIT_LOCATION_VIEW',
                        payload: {
                            location: toJS(location),
                            fieldIdentifier: `${workspacePanel.uuid}:EVENT_LOCATION`,
                        },
                    },
                ]);
            }
        },
    });

    const {
        saveEventMutation,
        saveEvent,
        transmitEvent,
        onSubmit,
        clearEvent,
        generatePDF,
        generateRen,
    } = useEventApi({
        eventMetadataStore,
        eventForm,
        onSaveEvent(eventPanelViews, saveContext) {
            const [
                eventPanelViewForCurrentEvent,
                ...eventPanelViewsForOtherEvents
            ] = eventPanelViews;
            refreshEventPanel({ eventPanelView: eventPanelViewForCurrentEvent });

            const savedAgencyEventNumber = eventPanelViewForCurrentEvent?.metadata?.agencyEventNumber?.trim();
            if (savedAgencyEventNumber && saveContext.isProQaRelated && proQaEnhancementsEnabled) {
                trigger([
                    {
                        id: v4(),
                        targetWindowType: 'EVENT_V2',
                        type: 'PRO_QA_CONSUME_PENDING_SECONDARY_LAUNCHES',
                        payload: {
                            agencyEventNumber: savedAgencyEventNumber,
                        },
                    },
                ]);
            }

            eventPanelViewsForOtherEvents.forEach((eventPanelView) => {
                openEventPanel({
                    eventId: eventPanelView.form.eventId,
                    shouldFocusPanel: true,
                });
            });
        },
        onClearEvent() {
            const { eventId, agencyId } = eventMetadataStore.state.metadata ?? {};
            if (
                eventId !== undefined &&
                agencyId !== undefined &&
                eventPanelOptions !== undefined
            ) {
                setEventPanelModal({
                    type: 'CLEAR_EVENT',
                    props: {
                        eventId,
                        agencyId,
                        refreshEventPanel,
                        eventPanelOptions,
                    },
                });
            }
        },
    });

    React.useEffect(() => {
        if (props.eventId && editableMode.mode !== 'view') {
            setEditableMode('view');
        }
        // a bug in useEditableMode means we can't use it as a dependency
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.eventId, editableMode]);

    React.useEffect(() => {
        return autorun(() => {
            const phoneCallDto = eventMetadataStore?.state.metadata?.eventPhoneCall;
            if (phoneCallDto) {
                phoneCallsStore.upsertHistoricPhoneCall(phoneCallDto);
            }
        });
    }, [phoneCallsStore, eventMetadataStore]);

    const removeEventNotifications = useRemoveEventNotifications();

    React.useEffect(() => {
        if (props.eventId) {
            removeEventNotifications({
                notifications: [
                    ...eventPanelNotificationGroupToNotifications['MAIN'].filter(
                        (notification) => !notificationBelongsToTab(notification)
                    ),
                ],
            });
        }
    }, [props.eventId, removeEventNotifications]);

    const assignedUnits = eventMetadataStore.state.metadata?.pillUnits ?? [];

    const eventIsEditable =
        // if there are assigned units and the user has permission to edit assigned events
        !(assignedUnits.length > 0 && !editAssignedEvents) &&
        // if there are not assigned units and the user has permission to edit unassigned events
        !(assignedUnits.length === 0 && !editUnassignedEvents) &&
        // if there is no metadata we assume the event is editable
        (!eventMetadataStore.state.metadata ||
            // new events are editable
            eventMetadataStore.state.metadata.agencyId === 0 ||
            // active events are editable
            eventMetadataStore.state.metadata.isActive === true ||
            // scheduled events are editable
            eventMetadataStore.state.metadata.isScheduledEvent === true ||
            // cleared events are editable for users with the EDIT_CLEARED_EVENTS ability
            (eventMetadataStore.state.metadata.isActive === false && editClearedEvents));

    return (
        <EventPanelUnitsProvider
            initialUnits={toJS(eventMetadataStore?.state.metadata?.pillUnits)}
            eventId={props.eventId}
        >
            <IntegratedFormProvider
                mode={eventIsEditable ? undefined : 'read-only'}
                form={eventForm}
                query={newOrExistingEventQuery}
                onSubmit={onSubmit}
                mutation={saveEventMutation}
                withEditable={true}
                editableMode={editableMode}
                inputSize="sm"
            >
                <EventMetadataProvider value={eventMetadataStore}>
                    <RefreshEventPanelProvider value={refreshEventPanel}>
                        <>
                            <EventPanelActionHandler
                                form={eventForm}
                                eventId={props.eventId}
                                panelUuid={workspacePanel.uuid}
                                refreshEventPanel={refreshEventPanel}
                                eventPanelOptions={eventPanelOptions!}
                                formHasHadDataSetAtLeastOnce={formHasHadDataSetAtLeastOnce}
                            />
                            <EventManagerRegister
                                clearEvent={clearEvent}
                                generatePDF={generatePDF}
                                generateRen={generateRen}
                                saveEvent={saveEvent}
                                transmitEvent={transmitEvent}
                            />
                            {props.children}
                        </>
                    </RefreshEventPanelProvider>
                </EventMetadataProvider>
            </IntegratedFormProvider>
        </EventPanelUnitsProvider>
    );
});
